import React, { useCallback, useState } from "react";
import PageArticle from "../../components/_template/page/PageArticle";
import PageTitle from "../../components/_molecules/page/PageTitle";
import BasicButton from "../../components/_atom/button/BasicButton";
import { ButtonSize, ButtonTheme } from "../../components/_atom/button/StyledButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import BasicTable from "../../components/_atom/table/BasicTable";
import { MyKit, MyKitCategory } from "../../assets/constant/kit.type";
import KitContainer from "./components/KitContainer";
import { logic } from "../../../logic";
import useLoading from "../../../hooks/useLoading";
import rightArrowIcon from "../../assets/images/arrow_right_btn_white.png";
import "../../styles/MyKits.scss";
import PageLoading from "../../components/_atom/loading/PageLoading";
import EmptyDataTemplate from "../dashboard/components/EmptyDataTemplate";
import { PagingBar } from "../../components/_atom/paging/PagingBar";
import TableStatus, { TABLE_STATUS } from "./components/TableStatus";
import nftKitImg from "../../assets/images/img_nft_kit.png";
import tokenKitImg from "../../assets/images/img_token_kit.png";
import membershipKitImg from "../../assets/images/img_membership_kit.png";
import { networkParserNew } from "../../../utils/Utils";

const KITS_COLGROUP = [2.5, 1.5, 3, 1];
const KITS_HEADERS = [
  { title: "Name", accessor: "name" },
  { title: "Category", accessor: "category" },
  { title: "Network", accessor: "network" },
  { title: "Status", accessor: "status" },
];
const KITS_LIMIT = 10;

const MyKits = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<MyKitCategory[]>([]);
  const getMyKitCategories = async () => {
    const { result, error } = await logic.myKit.getMyKitsCategoryList();
    if (error) {
      return;
    }
    const { rows } = result;
    const tokenKitCategory = rows.find((category) => category.category_id === 3);
    setCategories([tokenKitCategory as MyKitCategory]);
  };
  const { loading: categoryLoading } = useLoading({
    dependency: [],
    synchronousFunction: getMyKitCategories,
  });
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);

  const [myKits, setMyKits] = useState<MyKit[]>([]);
  const [totalKitCount, setTotalKitCount] = useState(0);
  const [searchParams] = useSearchParams();
  const pageNum = Number(searchParams.get("my_kit")) || 1;
  const maxPageNum = totalKitCount % KITS_LIMIT === 0 ? totalKitCount / KITS_LIMIT : Math.floor(totalKitCount / KITS_LIMIT) + 1;
  const getMyKits = async (option: { lastId?: number; offset?: number; order?: "ASC" | "DESC"; categoryIds?: number[] }) => {
    const { result, error } = await logic.myKit.getAllMyKits({
      limit: KITS_LIMIT,
      ...{
        ...option,
        categoryIds: [3],
      },
    });
    if (error) {
      return;
    }
    const { count, rows } = result;
    const { result: microChains, error: microChainsError } = await logic.microChain.getMicroChainList({});
    if (microChainsError) {
      return;
    }
    const networks = networkParserNew(microChains);
    const myKits = rows.map((kit) => {
      const network = networks.find((network) => network.id === kit.kit_data.chain_id);
      return {
        ...kit,
        kit_data: {
          ...kit.kit_data,
          network: network?.label,
        },
      };
    });
    setTotalKitCount(count);
    setMyKits(myKits);
  };
  const { loading } = useLoading({
    dependency: [selectedCategoryIds, pageNum],
    synchronousFunction: () =>
      getMyKits({
        offset: (pageNum - 1) * KITS_LIMIT,
        ...(selectedCategoryIds.length > 0 && {
          categoryIds: selectedCategoryIds,
        }),
      }),
  });
  const KITS_RENDERER = useCallback((data: MyKit) => {
    const status =
      data?.kit_data?.status === 4
        ? TABLE_STATUS.DEPLOYED
        : data?.kit_data?.status === 3
        ? TABLE_STATUS.PROCESSING
        : data?.kit_data?.status === 2
        ? TABLE_STATUS.PREPARED
        : TABLE_STATUS.CREATED;
    const categoryImage = () => {
      switch (data.category.category_id) {
        case 3:
          return tokenKitImg;
        case 1:
          return nftKitImg;
        case 2:
          return membershipKitImg;
      }
    };
    const category = (
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <img
          src={categoryImage()}
          alt=""
          style={{
            width: 24,
            height: 24,
            objectFit: "contain",
            borderRadius: 50,
          }}
        />
        <div>{data.category?.name}</div>
      </div>
    );
    return {
      name: (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={data.kit_data?.image}
            alt=""
            style={{
              width: 24,
              height: 24,
              objectFit: "contain",
              borderRadius: 50,
            }}
          />
          <div>{data.kit_data?.name}</div>
        </div>
      ),
      category,
      // network: data.kit_data?.network, //TODO:: 수정 필요
      network: data.kit_data?.network ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={data.kit_data?.origin_micro_chain_image}
            alt=""
            style={{
              width: 24,
              height: 24,
              objectFit: "contain",
              borderRadius: 50,
            }}
          />
          <div>{data.kit_data?.network}</div>
        </div>
      ) : (
        "-"
      ),
      status: <TableStatus status={status} />,
    };
  }, []);

  return (
    <PageArticle id="MyKits">
      <PageTitle
        title={"My Kits"}
        description={
          "Your Tokenization Command Center\nCheck your active tokenization kits for your project, access their features, or click 'Go to Tokenize' to create a new kit whenever you need to implement additional tokenization."
        }
      />
      <div style={{ padding: 40 }}>
        <KitContainer
          title={"Kit List"}
          Button={
            <BasicButton size={ButtonSize.DYNAMIC} theme={ButtonTheme.PRIMARY} onClick={() => navigate("/tokenization")}>
              Go to Tokenize
              <img src={rightArrowIcon} alt="" style={{ width: 18, height: 18, marginLeft: 5 }} />
            </BasicButton>
          }
        >
          {!loading && myKits.length === 0 && selectedCategoryIds.length === 0 ? (
            <EmptyDataTemplate>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 500,
                  lineHeight: "21px",
                  color: "#7D828D",
                }}
              >
                There are no Kits yet.
              </div>
            </EmptyDataTemplate>
          ) : (
            <>
              {categories.length > 0 && (
                <div className="category-btn-wrapper">
                  {categories.map((category) => {
                    const selected = selectedCategoryIds.includes(category.category_id);
                    const onClickCategory = () => {
                      if (selected) {
                        setSelectedCategoryIds((prev) => prev.filter((id) => id !== category.category_id));
                      } else {
                        setSelectedCategoryIds((prev) => [...prev, category.category_id]);
                      }
                    };
                    return (
                      <button key={category.category_id} className={`category-btn${selected ? " selected" : ""}`} onClick={onClickCategory} disabled={loading}>
                        {category.name}
                      </button>
                    );
                  })}
                </div>
              )}
              {loading ? (
                <PageLoading style={{ height: 277 }} />
              ) : (
                <>
                  {myKits.length === 0 ? (
                    <EmptyDataTemplate>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 500,
                          lineHeight: "21px",
                          color: "#7D828D",
                        }}
                      >
                        There are no Kits in these categories.
                      </div>
                    </EmptyDataTemplate>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                      }}
                    >
                      <BasicTable
                        data={myKits}
                        colGroup={KITS_COLGROUP}
                        headers={KITS_HEADERS}
                        renderer={KITS_RENDERER}
                        onClick={(kit: MyKit) => {
                          if (kit) {
                            const kitType = kit.category.category_id === 3 ? "token" : kit.category.category_id === 1 ? "nft" : "membership";
                            navigate("/tokenization/my-kits/" + kitType + "/" + kit.resource_identifier);
                          }
                        }}
                      />
                      <PagingBar maxPageNum={maxPageNum} query={"my_kit"} />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </KitContainer>
      </div>
    </PageArticle>
  );
};

export default MyKits;
